/**
 * CategorySearchBar Component - AliExpress + Etsy Style
 * 
 * Premium search bar for Student Hub category pages.
 * Features:
 * 1. Camera icon (left inside pill) - Opens bottom sheet for visual search
 * 2. Black search button (right) - Opens recent searches dropdown
 * 3. Clean pill border (no orange outline)
 * 4. Category-specific search history storage
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Search History helpers
const MAX_HISTORY_ITEMS = 10;

const getSearchHistoryKey = (category) => `wassla.student.${category}.searchHistory.v1`;

const loadSearchHistory = (category) => {
  try {
    const raw = localStorage.getItem(getSearchHistoryKey(category));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const saveSearchHistory = (category, items) => {
  localStorage.setItem(getSearchHistoryKey(category), JSON.stringify(items.slice(0, MAX_HISTORY_ITEMS)));
};

const addToSearchHistory = (category, term) => {
  const t = term.trim();
  if (!t) return;
  const current = loadSearchHistory(category);
  const next = [t, ...current.filter((x) => x.toLowerCase() !== t.toLowerCase())];
  saveSearchHistory(category, next);
};

export default function CategorySearchBar({
  category,
  placeholder = { en: 'Search...', ar: 'بحث...' },
  value = '',
  onChange,
  onSubmit,
  onFilterClick,
  hasActiveFilters = false,
}) {
  const { t, i18n } = useTranslation();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const dir = isRTL ? 'rtl' : 'ltr';
  
  // State
  const [recentOpen, setRecentOpen] = useState(false);
  const [recent, setRecent] = useState(() => loadSearchHistory(category));
  
  // Refs
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  
  const showClear = Boolean(value && value.length);
  
  const recentFiltered = useMemo(() => {
    return recent.filter(Boolean);
  }, [recent]);
  
  // Load search history on mount
  useEffect(() => {
    setRecent(loadSearchHistory(category));
  }, [category]);
  
  // Close dropdown on outside click / ESC
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setRecentOpen(false);
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setRecentOpen(false);
        inputRef.current?.blur();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  
  // Commit search
  const commitSearch = useCallback((q) => {
    const query = (q || '').trim();
    if (!query) return;
    
    addToSearchHistory(category, query);
    setRecent(loadSearchHistory(category));
    setRecentOpen(false);
    inputRef.current?.blur();
    
    onSubmit?.(query);
  }, [category, onSubmit]);
  
  // Handle key down
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSearch(value);
    }
  }, [commitSearch, value]);
  
  // Clear search
  const handleClear = useCallback(() => {
    onChange?.('');
    inputRef.current?.focus();
  }, [onChange]);
  
  return (
    <section className="catSearchSection" data-testid="category-search-section">
      <div className={`catSearchWrap ${isRTL ? 'rtl' : 'ltr'}`} dir={dir} ref={wrapperRef}>
        {/* Filter Button */}
        <button 
          type="button"
          className={`catSearchFilterBtn ${hasActiveFilters ? 'hasFilters' : ''}`}
          onClick={onFilterClick}
          data-testid="filter-btn"
          aria-label={t('home.filters')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M10 18h4v-2h-4v2zM3 11h18V9H3v2zm3 7h12v-2H6v2zM7 6V4h10v2H7z"
            />
          </svg>
          {hasActiveFilters && <span className="catSearchFilterDot" />}
        </button>

        {/* Pill Container */}
        <div className="catSearchPill">
          {/* Search Icon inside pill */}
          <span className="catSearchIconStart" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M10 2a8 8 0 1 0 4.9 14.3l4.4 4.4 1.4-1.4-4.4-4.4A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12z"
              />
            </svg>
          </span>

          <input
            ref={inputRef}
            className="catSearchInput"
            type="text"
            value={value}
            onChange={(e) => {
              onChange?.(e.target.value);
              setRecentOpen(true);
            }}
            onFocus={() => setRecentOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={isRTL ? placeholder.ar : placeholder.en}
            inputMode="search"
            autoComplete="off"
            data-testid="search-input"
          />

          {/* Clear Button */}
          {showClear && (
            <button
              type="button"
              className="catSearchClearBtn"
              onClick={handleClear}
              aria-label={t('psb.clear')}
              data-testid="clear-search"
            >
              ✕
            </button>
          )}

          {/* Recent Searches Dropdown */}
          {recentOpen && (
            <div className="catSearchRecent" data-testid="search-recent">
              <div className="catSearchRecentHead">
                <span className="catSearchRecentTitle">
                  {t('home.recentSearches')}
                </span>
                <button
                  type="button"
                  className="catSearchRecentClose"
                  onClick={() => setRecentOpen(false)}
                  aria-label={t('rolesele.close')}
                >
                  ✕
                </button>
              </div>
              
              {recentFiltered.length === 0 ? (
                <div className="catSearchRecentEmpty">
                  {t('psb.noRecentSearchesYet')}
                </div>
              ) : (
                <ul className="catSearchRecentList">
                  {recentFiltered.map((q, idx) => (
                    <li key={`${q}-${idx}`}>
                      <button
                        type="button"
                        className="catSearchRecentItem"
                        onClick={() => {
                          onChange?.(q);
                          commitSearch(q);
                        }}
                        data-testid={`recent-item-${idx}`}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" className="catSearchRecentIcon">
                          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path stroke="currentColor" strokeWidth="2" d="M12 6v6l4 2"/>
                        </svg>
                        <span>{q}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              
              {recentFiltered.length > 0 && (
                <button
                  type="button"
                  className="catSearchRecentClearAll"
                  onClick={() => {
                    setRecent([]);
                    saveSearchHistory(category, []);
                  }}
                  data-testid="clear-all-recent"
                >
                  {t('home.clearAll')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export { loadSearchHistory, saveSearchHistory, addToSearchHistory, getSearchHistoryKey };
